import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  Text,
  tokens,
} from '@fluentui/react-components';
import { generateCatchupTasks, type CatchupGenerationSummary } from '../../services/complianceApi';
import { formatDate, formatWholeDay, wholeDayParts } from '../../utils/complianceFormatters';
import { CalendarDateInput } from '../../components/dates/CalendarDateInput';

/**
 * Format a date-ONLY value (yyyy-MM-dd, or an ISO datetime whose date part carries the
 * meaning — backend range dates are UTC-midnight date semantics) without the timezone
 * shift `new Date('yyyy-MM-dd')` causes for US-evening users (Mar 15 rendering as Mar 14).
 * Falls back to the shared formatDate for anything unparsable.
 */
function formatDateOnly(value: string): string {
  return wholeDayParts(value) ? formatWholeDay(value) : formatDate(value);
}

/**
 * STX-02 — admin-only "Generate historical tasks" confirm dialog (client detail page).
 *
 * Two-phase, mirroring the End-obligation dialog: a confirm form (shows the range that
 * will be examined + the safety copy: filed history untouched, existing tasks merged
 * not duplicated — re-running is safe), then a summary of what the run did (periods
 * examined / created / merged / skipped-existing). The from date shown IS the from date
 * sent, so what the admin confirmed is exactly what runs; the backend clamps it to the
 * 2026-01-01 history floor either way.
 */
export function CatchupGenerationDialog({
  open,
  onOpenChange,
  stateTaxClientId,
  defaultFromDate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateTaxClientId: string;
  /** Default range start (yyyy-MM-dd): earliest obligation effective-start, floored at 2026-01-01. */
  defaultFromDate: string;
}) {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [isPending, setIsPending] = useState(false);
  const [summary, setSummary] = useState<CatchupGenerationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  function close() {
    if (isPending) return;
    setSummary(null);
    setError(null);
    setFromDate(defaultFromDate);
    onOpenChange(false);
  }

  async function confirm() {
    setError(null);
    setIsPending(true);
    try {
      const result = await generateCatchupTasks(stateTaxClientId, fromDate || undefined);
      setSummary(result);
    } catch (err) {
      const serverMsg = err instanceof Error && err.message ? ` ${err.message}` : '';
      setError(`${t('clientDetailPage.obligations.catchup.failed')}${serverMsg}`);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_e, d) => { if (!d.open) close(); }}>
      <DialogSurface>
        {summary !== null ? (
          <DialogBody>
            <DialogTitle>{t('clientDetailPage.obligations.catchup.doneTitle')}</DialogTitle>
            <DialogContent>
              <Text>
                {t('clientDetailPage.obligations.catchup.doneRange', {
                  from: formatDateOnly(summary.fromDate),
                  to: formatDateOnly(summary.toDate),
                })}
              </Text>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto max-content',
                  columnGap: tokens.spacingHorizontalXL,
                  rowGap: tokens.spacingVerticalXS,
                  marginTop: tokens.spacingVerticalM,
                  marginBottom: 0,
                }}
              >
                <dt>{t('clientDetailPage.obligations.catchup.periodsExamined')}</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{summary.periodsExamined}</dd>
                <dt>{t('clientDetailPage.obligations.catchup.tasksCreated')}</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{summary.tasksCreated}</dd>
                <dt>{t('clientDetailPage.obligations.catchup.tasksMerged')}</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{summary.tasksMerged}</dd>
                <dt>{t('clientDetailPage.obligations.catchup.tasksSkipped')}</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{summary.tasksSkippedExisting}</dd>
              </dl>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={close}>
                {t('clientDetailPage.obligations.catchup.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        ) : (
          <DialogBody>
            <DialogTitle>{t('clientDetailPage.obligations.catchup.title')}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <Text>
                  {t('clientDetailPage.obligations.catchup.rangeInfo', {
                    from: formatDateOnly(fromDate),
                  })}
                </Text>
                <Field label={t('clientDetailPage.obligations.catchup.fromLabel')}>
                  <CalendarDateInput value={fromDate} min="2026-01-01" onChange={setFromDate} />
                </Field>
                <MessageBar intent="info">
                  <MessageBarBody>
                    {t('clientDetailPage.obligations.catchup.warning')}
                  </MessageBarBody>
                </MessageBar>
                {error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={isPending} onClick={close}>
                {t('clientDetailPage.obligations.catchup.cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={isPending || !fromDate}
                onClick={() => { void confirm(); }}
              >
                {t('clientDetailPage.obligations.catchup.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        )}
      </DialogSurface>
    </Dialog>
  );
}
